import { useJournalEntriesModalArgs } from '@vrplatform/embed/journal-entries-modal';
import {
  useAuditEffectsModalWhere,
  useDashboard,
  useTeam,
  useTeamRole,
} from '@vrplatform/data';
import {
  AppShell,
  Box,
  LoadingOverlay,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import {
  type ComponentType,
  type ReactNode,
  Suspense,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { DRAWER_PARAMS } from '../utils/remove-drawer-state-from-url';
import { parseAsString, useQueryStates } from '@vrplatform/query-params';
import { ErrorBoundary, PmErrorIssueNotification } from '../components';
import { ChatVRTDock, useChatVRTVisibility } from './chatvrt';
import { VRPilotGuideProvider } from '@vrtrust/components/vrpilot-guide/VRPilotGuideProvider';
import { useNavbarExpanded } from '../hooks';
import { useExtractModal } from '../modals/extract-modal/useExtractModal';
import { lazyWithRetry } from '../routes/dashboard/_utils';
import { LoadOnOpen } from './LoadOnOpen';
import { Header } from './Header';
import { OwnerImpersonationNotification } from './OwnerImpersonationNotification';
import { Navbar, type NavbarBaseProps } from './navbar';

const DRAWER_STATE_PARSERS = Object.fromEntries(
  [
    ...DRAWER_PARAMS,
    'activityEvent',
    'activityFrom',
    'activityRecordType',
    'activityRecordId',
    'connectionDetailId',
    'workflowIds',
    'add-team-drawer',
  ].map((param) => [param, parseAsString])
);

const VRPilotGuideController = lazyWithRetry(() =>
  import('@vrtrust/components/vrpilot-guide').then((module) => ({
    default: module.VRPilotGuideController,
  }))
);
const VrpAdvent = lazyWithRetry(() =>
  import('../components/vrp-advent/VrpAdvent').then((module) => ({
    default: module.VrpAdvent,
  }))
);
const ExtractModal = lazyWithRetry(() =>
  import('../modals/extract-modal').then((module) => ({
    default: module.ExtractModal,
  }))
);
const JournalEntriesModal = lazyWithRetry(() =>
  import('@vrtrust/modals/journal-entries-modal').then((module) => ({
    default: module.JournalEntriesModal,
  }))
);
const AuditEffectsModal = lazyWithRetry(() =>
  import('@vrtrust/modals/audit-effects-modal').then((module) => ({
    default: module.AuditEffectsModal,
  }))
);

export const AppLayout = ({
  children,
  drawers,
  ...navbarProps
}: {
  children: ReactNode;
  drawers: ComponentType[];
} & NavbarBaseProps) => {
  const [{ isGL: GL, settings }] = useTeam();
  const { isVrpAdmin } = useTeamRole();
  const { opened: extractModalOpened } = useExtractModal();
  const journalEntriesModalArgs = useJournalEntriesModalArgs();
  const auditEffectsModalWhere = useAuditEffectsModalWhere();
  const { active: chatVRTActive, available: chatVRTAvailable } =
    useChatVRTVisibility();

  const {
    isDesktopExpanded: isExpanded,
    isMobile,
    mobileBreakpoint,
  } = useNavbarExpanded();
  const [dashboard] = useDashboard();
  const { colorScheme } = useMantineColorScheme();
  const location = useLocation();
  const theme = useMantineTheme();
  const navBgColor =
    colorScheme === 'dark'
      ? theme.colors.dark[8]
      : theme.colors[theme.primaryColor][8];

  // Drawer openers write the URL through nuqs without a router navigation,
  // so the router location does not see them; nuqs state does.
  const [drawerState] = useQueryStates(DRAWER_STATE_PARSERS);
  const hasDrawerParam = Object.values(drawerState).some(
    (value) => value !== null
  );
  const [collapsedOnMobile, setCollapsedOnMobile] = useState(false);
  const showVRPilotGuide =
    dashboard === 'propertyManager' && settings.showVRPilotGuide;
  // The owner portal settings editor needs the full content width, so its tab
  // is hidden there. The provider stays mounted so navigating to the
  // page does not remount the app content.
  const showVRPilotGuideController =
    showVRPilotGuide &&
    !location.pathname.startsWith('/settings/features/owner-portal');

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!collapsedOnMobile) {
      setCollapsedOnMobile(true);
    }
  }, [location.search, location.pathname]);

  const appContent = (
    <>
      <AppShell
        header={{
          height: 60,
          collapsed: !isMobile,
        }}
        navbar={{
          collapsed: {
            desktop: false,
            mobile: true,
          },
          breakpoint: mobileBreakpoint,
          width: isExpanded ? 310 : 110,
        }}
        aside={{
          breakpoint: mobileBreakpoint,
          collapsed: {
            desktop: true,
            mobile: collapsedOnMobile,
          },
          width: isExpanded ? 280 : 110,
        }}
        styles={(theme) => ({
          navbar: {
            width: isExpanded ? 280 : 80,
            transition: 'all 0.3s ease-in-out',
            height: `calc(100vh - 2*${theme.spacing.sm})`,
            margin: theme.spacing.sm,
            overflow: 'visible',
            border: 'none',
          },
          aside: {
            width: isExpanded ? 280 : 80,
            transition: 'all 0.3s ease-in-out',
            backgroundColor: navBgColor,
            height: `calc(100vh - 2*${theme.spacing.sm} - 55px)`,
            margin: theme.spacing.sm,
            marginRight: collapsedOnMobile ? 0 : theme.spacing.sm,
            borderRadius: theme.spacing.md,
            overflowX: 'hidden',
            border: 'none',
          },
        })}
      >
        <Header
          toggleNavbar={(e) => setCollapsedOnMobile((c) => e ?? !c)}
          opened={!collapsedOnMobile}
        />

        {isMobile ? (
          <AppShell.Aside
            bg={navBgColor}
            c="white"
            data-vrpilot-target={chatVRTAvailable ? 'navigation' : undefined}
          >
            <Navbar {...navbarProps} />
          </AppShell.Aside>
        ) : (
          <AppShell.Navbar bg="transparent" c="white">
            <Box
              sx={{
                alignItems: 'stretch',
                display: 'flex',
                height: '100%',
                width: '100%',
              }}
            >
              <Box
                bg={navBgColor}
                c="white"
                data-vrpilot-target={
                  chatVRTAvailable ? 'navigation' : undefined
                }
                sx={(theme) => ({
                  borderRadius: theme.spacing.md,
                  display: 'flex',
                  flex: '0 0 auto',
                  flexDirection: 'column',
                  height: '100%',
                  overflowX: 'hidden',
                  width: isExpanded ? 280 : 80,
                })}
              >
                <Navbar {...navbarProps} />
              </Box>
              {showVRPilotGuideController && (
                <Suspense fallback={null}>
                  <VRPilotGuideController />
                </Suspense>
              )}
            </Box>
          </AppShell.Navbar>
        )}

        <AppShell.Main
          data-vrpilot-target={chatVRTAvailable ? 'main-content' : undefined}
          mt={isMobile ? '60px' : undefined}
          py={isMobile ? 'xs' : 'sm'}
          pr={isMobile ? 'xs' : 'sm'}
          pl={isMobile ? 'xs' : undefined}
          display="flex"
          h="100vh"
          mih="100vh"
          mah="100vh"
          style={{
            flexDirection: 'column',
            position: 'relative',
            overflowX: 'hidden',
          }}
        >
          {isVrpAdmin && (
            <Suspense fallback={null}>
              <VrpAdvent />
            </Suspense>
          )}
          <ErrorBoundary>{children}</ErrorBoundary>
        </AppShell.Main>
      </AppShell>

      {/* // ** Dynamically added drawers depending on app */}
      <LoadOnOpen open={hasDrawerParam}>
        {drawers.map((Drawer, i) => (
          <Drawer key={i} />
        ))}
      </LoadOnOpen>

      <OwnerImpersonationNotification />
      {chatVRTActive && (
        <Suspense fallback={null}>
          <ChatVRTDock dev />
        </Suspense>
      )}
    </>
  );

  const appContentWithVRPilotGuide =
    showVRPilotGuide && !isMobile ? (
      <VRPilotGuideProvider>{appContent}</VRPilotGuideProvider>
    ) : (
      appContent
    );

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingOverlay visible />}>
        {appContentWithVRPilotGuide}

        {dashboard === 'propertyManager' && !isMobile && (
          <PmErrorIssueNotification type="affix" />
        )}

        <LoadOnOpen open={extractModalOpened}>
          <ExtractModal />
        </LoadOnOpen>
        {GL && (
          <>
            <LoadOnOpen open={!!journalEntriesModalArgs}>
              <JournalEntriesModal />
            </LoadOnOpen>
            <LoadOnOpen open={!!auditEffectsModalWhere}>
              <AuditEffectsModal />
            </LoadOnOpen>
          </>
        )}
      </Suspense>
    </ErrorBoundary>
  );
};
